import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from "react-markdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Disclaimer from "@/components/Disclaimer";
import {
  Lock, ShieldAlert, Eye, Sparkles, Activity, Clock, ArrowRight, Trophy, Flame, Info,
  Bot, UserCheck, BadgeCheck, Link2, RefreshCw, BookOpen, HelpCircle,
} from "lucide-react";

import { usePublicArticlesEnabled } from "@/hooks/usePublicArticlesEnabled";
import { buildStructuredData, sportInfo, recommendedInternalLinks } from "@/lib/articleSeo";

type Article = {
  id: string;
  pick_id: string;
  sport: string | null;
  slug: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  excerpt: string | null;
  article_body_md: string | null;
  status: string;
  pick_snapshot: any;
  published_at: string | null;
  created_at: string;
  updated_at?: string | null;
  reviewed_at?: string | null;
  ai_generated_at?: string | null;
  validation_error: string | null;
};

const SITE = "https://aleaedge.com";

export default function ArticlePage() {
  const params = useParams();
  const navigate = useNavigate();
  const slug = [params.sportSlug, params.slug].filter(Boolean).join("/");
  const [article, setArticle] = useState<Article | null>(null);
  const [related, setRelated] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const { enabled: publicArticlesEnabled, loading: settingLoading } = usePublicArticlesEnabled();

  // noindex management
  useEffect(() => {
    if (settingLoading) return;
    if (!publicArticlesEnabled) {
      const meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex,nofollow";
      document.head.appendChild(meta);
      return () => { document.head.removeChild(meta); };
    }
  }, [publicArticlesEnabled, settingLoading]);

  // Load article + related
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("pick_articles" as any)
        .select("id, pick_id, sport, slug, title, meta_title, meta_description, excerpt, article_body_md, status, pick_snapshot, published_at, created_at, updated_at, reviewed_at, ai_generated_at, validation_error")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (!active) return;
      let safe = (data && (data as any).status === "published") ? (data as any as Article) : null;
      if (!safe) {
        const { data: oldSlugData } = await supabase
          .from("pick_article_slugs" as any)
          .select("article_id")
          .eq("slug", slug)
          .maybeSingle();
        const oldSlug = oldSlugData as any;
        if (oldSlug?.article_id) {
          const { data: moved } = await supabase
            .from("pick_articles" as any)
            .select("id, pick_id, sport, slug, title, meta_title, meta_description, excerpt, article_body_md, status, pick_snapshot, published_at, created_at, updated_at, reviewed_at, ai_generated_at, validation_error")
            .eq("id", oldSlug.article_id)
            .eq("status", "published")
            .maybeSingle();
          safe = (moved && (moved as any).status === "published") ? (moved as any as Article) : null;
          if (safe && safe.slug !== slug) navigate(`/${safe.slug}`, { replace: true });
        }
      }
      setArticle(safe);
      if (safe) {
        document.title = safe.meta_title || safe.title;
        // meta description
        upsertMeta("description", safe.meta_description || safe.excerpt || "");
        upsertLink("canonical", `${SITE}/${safe.slug}`);
        // Related (same sport, exclude current)
        if (safe.sport) {
          const { data: rel } = await supabase
            .from("pick_articles" as any)
            .select("id, pick_id, sport, slug, title, meta_title, meta_description, excerpt, article_body_md, status, pick_snapshot, published_at, created_at, validation_error")
            .eq("status", "published")
            .eq("sport", safe.sport)
            .neq("id", safe.id)
            .order("published_at", { ascending: false })
            .limit(4);
          if (active) setRelated((rel as any) ?? []);
        }
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [slug]);

  // JSON-LD structured data (Article + SportsEvent + BreadcrumbList + FAQPage).
  // Premium reasoning, confidence matrix, and unit rating are intentionally NEVER
  // included in structured data — only the public, pre-paywall content.
  useEffect(() => {
    if (!article || !publicArticlesEnabled) return;
    const sd = buildStructuredData(article as any, SITE);
    const faq = buildFaqs(article).map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    }));
    const faqLd = faq.length ? {
      "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq,
    } : null;

    const nodes: HTMLScriptElement[] = [];
    for (const obj of [sd.breadcrumb, sd.article, sd.sportsEvent, faqLd].filter(Boolean) as object[]) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      nodes.push(s);
    }
    return () => { nodes.forEach((n) => n.remove()); };
  }, [article, publicArticlesEnabled]);

  if (loading) return <ArticleSkeleton />;

  if (!article) {
    return (
      <div className="container py-16">
        <Card className="te-card border-0">
          <CardContent className="py-16 text-center">
            <Info className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <h1 className="text-2xl font-display font-bold">Article not found</h1>
            <p className="text-sm text-muted-foreground mt-1">This preview may have been moved or unpublished.</p>
            <Button asChild className="mt-4"><Link to="/picks">Browse picks</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const snap = article.pick_snapshot ?? {};
  const isAdminPreview = !publicArticlesEnabled;
  const sport = article.sport ?? snap.sport ?? "Sports";
  const datePub = article.published_at ?? article.created_at;
  const dateMod = article.updated_at ?? article.reviewed_at ?? datePub;
  const oddsCaptured = snap.generated_at ?? article.ai_generated_at;
  const faqs = buildFaqs(article);
  const sportMeta = sportInfo(sport);
  const hubLinks = recommendedInternalLinks(article as any);

  return (
    <div className="min-h-screen">
      {/* Admin Preview Banner */}
      {isAdminPreview && (
        <div className="border-b border-accent/30 bg-accent/10">
          <div className="container py-2.5 flex flex-wrap items-center gap-3 justify-between text-xs">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-accent" />
              <span className="font-semibold text-accent uppercase tracking-widest">
                Admin Preview — Not Publicly Indexed
              </span>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> noindex, nofollow</span>
              <span>Toggle in Admin → Articles</span>
            </div>
          </div>
        </div>
      )}

      <article className="container py-8 md:py-12 max-w-4xl">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary">Home</Link></li>
            <li>/</li>
            <li><Link to={sportMeta.category} className="hover:text-primary">{sportMeta.label} Picks</Link></li>
            <li>/</li>
            <li className="text-foreground/80 truncate max-w-[40ch]">{article.title}</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[10px] uppercase tracking-widest border-primary/40 text-primary">
              {sport}
            </Badge>
            <Badge variant="outline" className="text-[10px] uppercase tracking-widest">
              {snap.market ?? "Market"}
            </Badge>
            <Badge variant="outline" className="text-[10px] uppercase tracking-widest">
              {article.status === "published" ? "Live" : `Status: ${article.status}`}
            </Badge>
          </div>

          <h1 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-[1.1]">
            {article.title}
          </h1>

          {article.excerpt && (
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl">{article.excerpt}</p>
          )}

          {/* Trust + freshness signals */}
          <div className="flex flex-wrap items-center gap-2">
            <TrustChip icon={<Bot className="w-3 h-3" />} label="AI Generated" tone="primary" />
            {article.reviewed_at && <TrustChip icon={<UserCheck className="w-3 h-3" />} label="Human Reviewed" tone="accent" />}
            {article.published_at && <TrustChip icon={<BadgeCheck className="w-3 h-3" />} label="Editorially Approved" tone="primary" />}
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> AleaEdge Editorial</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />
              Published {new Date(datePub).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "long", day: "numeric", year: "numeric" })}
            </span>
            {dateMod && dateMod !== datePub && (
              <span className="inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" />
                Updated {new Date(dateMod).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric" })}
              </span>
            )}
            {oddsCaptured && (
              <span className="inline-flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Odds captured {new Date(oddsCaptured).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} PT
              </span>
            )}
            {snap.game_start_time || snap.game_time ? (
              <span className="inline-flex items-center gap-1.5">
                <Trophy className="w-3.5 h-3.5" /> Game {new Date(snap.game_start_time ?? snap.game_time).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} PT
              </span>
            ) : null}
          </div>
        </header>

        {/* Matchup Snapshot Card */}
        <Card className="te-card border-0 mt-6 overflow-hidden">
          <div className="bg-gradient-to-br from-primary/10 via-transparent to-accent/10 p-5 md:p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-3">
              <Sparkles className="w-3.5 h-3.5 text-primary" /> Matchup Snapshot
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Snap label="Teams" value={snap.teams ?? "—"} />
              <Snap label="Market" value={String(snap.market ?? "—")} />
              <Snap label="Line / Odds" value={`${snap.line ?? "—"} @ ${snap.odds ?? "—"}`} />
              <Snap label="Risk" value={String(snap.risk ?? "—").toUpperCase()} />
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-2 gap-4">
              <div className="te-card p-4 bg-background/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Confidence Teaser</div>
                <div className="text-2xl font-display font-bold mt-1 te-grad-text">
                  {snap.confidence_score ?? "—"}<span className="text-base text-muted-foreground">/100</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">Full breakdown gated to AleaEdge subscribers.</div>
              </div>
              <div className="te-card p-4 bg-background/40">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Suggested Units</div>
                <div className="text-2xl font-display font-bold mt-1">
                  {snap.unit_rating ?? "—"}<span className="text-base text-muted-foreground">u</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">Premium analysis includes confidence matrix.</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Body */}
        <section className="mt-8">
          <div className="prose prose-invert prose-base md:prose-lg max-w-none
                          prose-headings:font-display prose-headings:tracking-tight
                          prose-h2:mt-10 prose-h2:mb-3 prose-h2:text-2xl
                          prose-h3:mt-6 prose-h3:mb-2
                          prose-p:leading-relaxed prose-p:text-foreground/90
                          prose-strong:text-foreground
                          prose-a:text-primary hover:prose-a:underline
                          prose-li:my-1
                          prose-hr:border-border">
            <ReactMarkdown>{article.article_body_md ?? ""}</ReactMarkdown>
          </div>
        </section>

        {/* Locked premium */}
        <LockedPremium />

        {/* FAQ */}
        {faqs.length > 0 && (
          <section className="mt-10" aria-labelledby="faq-heading">
            <div className="flex items-center gap-2 mb-3">
              <HelpCircle className="w-4 h-4 text-primary" />
              <h2 id="faq-heading" className="text-xl md:text-2xl font-display font-bold">Frequently Asked Questions</h2>
            </div>
            <Accordion type="single" collapsible className="te-card divide-y divide-border/40 overflow-hidden">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="px-4 border-0">
                  <AccordionTrigger className="text-left text-sm md:text-base font-semibold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {/* Internal Links Hub */}
        <section className="mt-10" aria-labelledby="hub-heading">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-4 h-4 text-primary" />
            <h2 id="hub-heading" className="text-xl md:text-2xl font-display font-bold">Explore AleaEdge</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {hubLinks.map((l) => (
              <HubLink key={l.href} to={l.href} title={l.label} desc="" />
            ))}
          </div>
        </section>

        {/* Related Articles */}
        {related.length > 0 && (
          <section className="mt-10" aria-labelledby="related-heading">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-4 h-4 text-accent" />
              <h2 id="related-heading" className="text-xl md:text-2xl font-display font-bold">Related Picks &amp; Articles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {related.map((r) => (
                <Link key={r.id} to={`/${r.slug}`} className="te-card p-4 group hover:border-primary/40 transition-colors">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-2">
                    <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-primary/30 text-primary/90">{r.sport ?? sport}</Badge>
                    {r.published_at && <span>{new Date(r.published_at).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric" })}</span>}
                  </div>
                  <div className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-2">{r.title}</div>
                  {r.excerpt && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.excerpt}</p>}
                  <div className="text-[11px] text-primary mt-2 inline-flex items-center gap-1 opacity-80 group-hover:opacity-100">
                    Read analysis <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Disclaimer */}
        <div className="mt-10 te-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Responsible play</div>
          <Disclaimer />
        </div>

        {isAdminPreview && (
          <p className="text-[11px] text-muted-foreground/70 mt-6 text-center">
            Canonical URL, OpenGraph, Twitter cards, and JSON-LD are staged but not emitted while public articles are disabled in Admin → Articles.
          </p>
        )}
      </article>
    </div>
  );
}

// ---------- helpers ----------

function upsertMeta(name: string, content: string) {
  let el = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
  el.content = content;
}
function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) { el = document.createElement("link"); el.rel = rel; document.head.appendChild(el); }
  el.href = href;
}

function buildFaqs(article: Article): { q: string; a: string }[] {
  const snap = article.pick_snapshot ?? {};
  const teams = snap.teams || "this matchup";
  const market = snap.market || "this market";
  const sport = article.sport ?? snap.sport ?? "the sport";
  const line = snap.line ?? "—";
  const odds = snap.odds ?? "—";
  const out: { q: string; a: string }[] = [];

  out.push({
    q: `What is AleaEdge's pick for ${teams}?`,
    a: `AleaEdge's editorial preview covers ${market} for ${teams}. The free preview includes the matchup snapshot, market context and risk factors. The full confidence matrix, unit rating and premium reasoning are gated to AleaEdge subscribers.`,
  });
  if (line !== "—" || odds !== "—") {
    out.push({
      q: `What are the current odds for ${teams}?`,
      a: `At capture time the ${market} market shows a line of ${line} at ${odds}. Odds move quickly — confirm with your sportsbook before placing any wager.`,
    });
  }
  out.push({
    q: `How does AleaEdge generate ${sport} picks?`,
    a: `Picks are produced from a structured market and matchup model, then reviewed by AleaEdge editorial before publication. We never invent injuries, lineups, or stats — when data is limited we say so explicitly.`,
  });
  out.push({
    q: `Are AleaEdge picks guaranteed?`,
    a: `No. All analysis is informational and entertainment only. No outcome is guaranteed. Bet responsibly and only what you can afford to lose.`,
  });
  return out;
}

function TrustChip({ icon, label, tone }: { icon: React.ReactNode; label: string; tone: "primary" | "accent" }) {
  const cls = tone === "primary"
    ? "border-primary/40 text-primary bg-primary/10"
    : "border-accent/40 text-accent bg-accent/10";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${cls}`}>
      {icon}{label}
    </span>
  );
}

function HubLink({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link to={to} className="te-card p-3 group hover:border-primary/40 transition-colors">
      <div className="text-sm font-semibold group-hover:text-primary transition-colors">{title}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{desc}</div>
    </Link>
  );
}

function Snap({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm md:text-base font-semibold mt-1 truncate">{value}</div>
    </div>
  );
}

function LockedPremium() {
  return (
    <section className="mt-10 relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background to-accent/10">
      <div className="absolute inset-0 pointer-events-none opacity-50"
           style={{ background: "radial-gradient(circle at 30% 20%, hsl(var(--primary)/0.25), transparent 60%)" }} />
      <div className="relative p-6 md:p-10">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary mb-3">
          <Lock className="w-4 h-4" /> Premium Section
        </div>
        <h3 className="text-2xl md:text-3xl font-display font-bold">Unlock Full AleaEdge Analysis</h3>
        <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-2xl">
          Get the complete market edge, confidence matrix, unit rating, and the full premium reasoning behind
          this matchup.
        </p>

        <div className="relative mt-6">
          <div className="select-none blur-sm pointer-events-none space-y-2 text-sm text-foreground/70">
            <p>• Confidence matrix: line value, recent form, matchup edge, injuries, market movement…</p>
            <p>• Historical hit rate by similar setups, with risk-adjusted unit recommendation.</p>
            <p>• Sportsbook-specific shading and anticipated line move windows.</p>
            <p>• Full AleaEdge lean with reasoning trail and confidence band.</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <Button asChild size="lg" className="bg-gradient-primary text-primary-foreground shadow-glow">
            <Link to="/pricing">
              <Lock className="w-4 h-4" /> Unlock Full Analysis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/picks">Browse free picks</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

function ArticleSkeleton() {
  return (
    <div className="container py-10 max-w-4xl space-y-4">
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-40 w-full mt-6" />
      <Skeleton className="h-72 w-full" />
    </div>
  );
}
